import { Typography, useTheme } from '@forgeglobal/design-framework';
import { Doughnut } from 'react-chartjs-2';
import {
  ArcElement,
  Chart,
  ChartData,
  ChartOptions,
  registerables,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { sum } from 'lodash';
import { useStyles } from './style';

Chart.register(...registerables, ArcElement, ChartDataLabels, Legend);

const DISPLAY_THRESHOLD = 0.05;

export interface Slice {
  value: number;
  label: string;
  color: string;
}

export interface PieChartProps {
  slices: Slice[];
}

const PieChart = ({ slices }: PieChartProps) => {
  const theme = useTheme();
  const classes = useStyles();

  const totalValue = sum(slices.map((s) => s.value));
  const values: number[] = [];
  const labels: string[] = [];
  const colors: string[] = [];

  slices.forEach((s) => {
    if (s.value > 0) {
      values.push(s.value);
      labels.push(s.label);
      colors.push(s.color);
    }
  });

  // calculate percentages
  const percentages = values.map((value) => value / totalValue);

  // construct data sets and labels for chart
  // @TODO: the template parameter 'doughnut' really shouldn't be necessary
  const chartData: ChartData<'doughnut'> = {
    labels: labels ?? [],
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderWidth: 2,
      },
    ],
  };

  // set other chart options, including legend and segment labels
  const chartOptions: ChartOptions<'doughnut'> = {
    maintainAspectRatio: false,
    events: ['mousemove', 'mouseout', 'touchstart', 'touchmove'],
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: theme.palette.common.white,
        anchor: 'center',
        font: {
          family: theme.typography.fontFamily,
          size: parseFloat(theme.spacing(1.5)),
        },
        display: percentages.map(
          (percentage) => percentage >= DISPLAY_THRESHOLD
        ),
        formatter: (value, context) =>
          `${(percentages[context.dataIndex] * 100).toFixed(1)}%`,
      },
    },
  };

  if (slices.length === 0 || totalValue < 0.01) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        className={classes.noResults}>
        There is no data to display
      </Typography>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.chartWrapper}>
        <Doughnut data={chartData} options={chartOptions} />
      </div>
      <div className={classes.legendWrapper}>
        <div className={classes.legend}>
          {slices.map((s) => (
            <div className={classes.legendRow} key={s.label} title={s.label}>
              <div
                className={classes.colorIcon}
                style={{ backgroundColor: s.color }}></div>
              <span className={classes.legendText}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PieChart;
